import {
  inventoryMutations,
  mutationBlobKey,
} from './inventoryMutationCoordinator';
import {
  deleteMultipleItems,
  updateMultipleItems,
} from '../firebase/functions';
import {
  CollectionBulkUpdateResult,
  CollectionPermanentDeleteResult,
  InventoryAuthority,
  inventoryFirestorePath,
} from '@qkindex/contracts';
import { imageOperationService } from './imageOperationService';

export const MAX_DIRECT_BULK_ITEMS = 400;
export const DIRECT_BULK_LIMIT_MESSAGE = `Bulk actions support at most ${MAX_DIRECT_BULK_ITEMS} items. Select a smaller set.`;

export type BulkItemUpdates = Record<
  string,
  string | number | boolean | string[] | undefined
>;

export interface BulkOperationResult {
  success: boolean;
  operationId?: string;
  idempotent?: boolean;
  details?: Record<string, number>;
}

const assertDirectLimit = (count: number): void => {
  if (count < 1) throw new Error('Select at least one item.');
  if (count > MAX_DIRECT_BULK_ITEMS) throw new Error(DIRECT_BULK_LIMIT_MESSAGE);
};

export const bulkOperationService = {
  async deleteItems(
    authority: InventoryAuthority,
    itemIds: string[],
    suppliedOperationId?: string,
  ): Promise<BulkOperationResult> {
    itemIds = [...new Set(itemIds)].sort();
    assertDirectLimit(itemIds.length);
    inventoryFirestorePath(authority, 'items');

    return inventoryMutations.run(
      authority.userId,
      [{ collectionId: authority.collectionId, itemIds }],
      JSON.stringify(['delete', authority.collectionId, [...itemIds].sort()]),
      'Delete items',
      async (operationId) => {
        const response = await deleteMultipleItems({
          collectionId: authority.collectionId,
          operationId,
          itemIds,
        });
        const result = response.data as CollectionPermanentDeleteResult;
        return {
          success: true,
          operationId: result.operationId,
          idempotent: result.idempotent,
          details: {
            itemsDeleted: result.details.itemsDeleted,
            missingItems: result.details.missingItems,
            cleanupImageCount: result.details.cleanupImageCount,
            filesDeleted: result.details.filesDeleted,
            retainedImageCount: result.details.retainedImageCount,
            cleanupComplete: result.details.cleanupComplete ? 1 : 0,
          },
        };
      },
      suppliedOperationId,
    );
  },
  async updateItems(
    authority: InventoryAuthority,
    items: Array<{
      id: string;
      updates: BulkItemUpdates;
      expectedRevision?: number;
    }>,
    suppliedOperationId?: string,
  ): Promise<BulkOperationResult> {
    assertDirectLimit(new Set(items.map(({ id }) => id)).size);
    inventoryFirestorePath(authority, 'items');

    const payload = items
      .map((item) => ({
        id: item.id,
        ...(item.expectedRevision === undefined
          ? {}
          : { expectedRevision: item.expectedRevision }),
        updates: { ...item.updates },
      }))
      .sort((a, b) => a.id.localeCompare(b.id));
    return inventoryMutations.run(
      authority.userId,
      [
        {
          collectionId: authority.collectionId,
          itemIds: payload.map((item) => item.id),
        },
      ],
      JSON.stringify(['update', authority.collectionId, payload]),
      'Update items',
      async (operationId) => {
        const response = await updateMultipleItems({
          collectionId: authority.collectionId,
          operationId,
          items: payload,
        });
        const result = response.data as CollectionBulkUpdateResult;
        return {
          success: true,
          operationId: result.operationId,
          idempotent: result.idempotent,
          details: { itemsUpdated: result.itemsUpdated },
        };
      },
      suppliedOperationId,
    );
  },
  async applyImage(
    authority: InventoryAuthority,
    itemIds: string[],
    image: Blob,
    suppliedOperationId?: string,
  ): Promise<BulkOperationResult> {
    const uniqueIds = [...new Set(itemIds)].sort();
    assertDirectLimit(uniqueIds.length);
    inventoryFirestorePath(authority, 'items');
    return inventoryMutations.run(
      authority.userId,
      [{ collectionId: authority.collectionId, itemIds: uniqueIds }],
      JSON.stringify([
        'image',
        authority.collectionId,
        uniqueIds,
        mutationBlobKey(image),
      ]),
      'Replace item images',
      async (operationId) => {
        const result = await imageOperationService.submit<{
          itemsUpdated: number;
          idempotent?: boolean;
        }>(
          {
            action: 'replace-image',
            collectionId: authority.collectionId,
            operationId,
            itemIds: uniqueIds,
          },
          image,
        );
        return {
          success: true,
          operationId,
          idempotent: result.idempotent,
          details: { itemsUpdated: result.itemsUpdated },
        };
      },
      suppliedOperationId,
    );
  },
};
